import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useTitle } from "@/hooks/use-title";

const SECTIONS = [
  {
    title: "Who we are",
    body: [
      "Operating Reality is an independent media, education and consulting brand operated by Anna Krat (operatingreality.co.uk). For the purposes of UK GDPR, Operating Reality is the data controller for information collected through this website.",
      "You can reach us about anything on this page via the contact form or by replying to any Fieldnotes email.",
    ],
  },
  {
    title: "What we collect",
    list: [
      "Contact enquiries — your name, organisation, email address and the message you send. Used only to respond to your enquiry.",
      "Fieldnotes subscriptions — your email address, used only to send the emails you asked for.",
      "Analytics (optional) — anonymised usage data via Google Analytics 4, only if you choose “Allow analytics” in the consent banner.",
    ],
    after: "We deliberately collect as little as possible. There are no accounts, no tracking pixels in emails beyond what the email provider needs to deliver them, and no advertising profiles.",
  },
  {
    title: "Lawful basis",
    body: [
      "Contact enquiries and Fieldnotes emails are processed on the basis of your explicit request (you submitted the form). Optional analytics run on the basis of your consent, which you can withdraw at any time via the cookie preferences link in the footer.",
    ],
  },
  {
    title: "Where the data lives",
    list: [
      "MongoDB Atlas — stores enquiry and subscriber records.",
      "Render — hosts the application that processes forms.",
      "Resend — delivers Fieldnotes emails and enquiry notifications.",
      "Google Analytics 4 — optional, consent-based visit statistics.",
      "YouTube (privacy-enhanced mode) — embedded videos; no cookies are set until you press play.",
    ],
  },
  {
    title: "Cookies and storage",
    body: [
      "This site stores a single item in your browser (localStorage key “or-consent”) that remembers your analytics choice. It is not shared with anyone.",
      "If you allow analytics, Google Analytics sets its own cookies (_ga and related) to distinguish visits. If you choose “Essential only”, no analytics cookies are ever set.",
    ],
  },
  {
    title: "How long we keep it",
    body: [
      "Enquiry records are kept while the conversation is active and for a reasonable period afterwards. Fieldnotes addresses are kept until you unsubscribe — every email is a conversation you can leave by replying.",
    ],
  },
  {
    title: "Your rights",
    body: [
      "Under UK GDPR you can request a copy of your data, ask for it to be corrected or deleted, object to processing, or withdraw consent at any time. Use the contact form and it will be handled personally.",
      "You also have the right to complain to the Information Commissioner's Office (ico.org.uk).",
    ],
  },
  {
    title: "Changes",
    body: [
      "If this policy changes, the date below will move and material changes will be noted in Fieldnotes.",
      "Last updated: August 2026.",
    ],
  },
];

export default function Privacy() {
  useTitle("Privacy — Operating Reality");
  return (
    <main className="pt-16 lg:pt-[72px]" data-testid="privacy-page">
      <section className="relative overflow-hidden border-b border-line/50">
        <div className="bg-grid absolute inset-0 opacity-25" />
        <div className="wrap relative py-20 lg:py-28">
          <Reveal>
            <Link
              to="/"
              className="group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-glow"
              data-testid="privacy-back-link"
            >
              <ArrowLeft size={13} className="transition-transform group-hover:-translate-x-1" />
              Operating Reality
            </Link>
            <h1 className="mt-10 max-w-3xl text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl">
              Privacy, in plain language.
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              The short version: we collect only what you deliberately give us, analytics run
              only with your consent, and your data is never sold or shared for marketing.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="wrap py-16 lg:py-24">
        <div className="max-w-3xl">
          {SECTIONS.map((s, i) => (
            <Reveal key={s.title} delay={0.03 * i}>
              <div className="border-t border-line/50 py-10 first:border-t-0 first:pt-0" data-testid={`privacy-section-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <h2 className="flex items-baseline gap-4 text-xl font-medium tracking-tight text-slate-100">
                  <span className="font-mono text-xs tracking-[0.3em] text-glow/70">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {s.title}
                </h2>
                <div className="mt-5 space-y-4 lg:pl-12">
                  {(s.body || []).map((p, j) => (
                    <p key={j} className="text-sm leading-relaxed text-slate-400">
                      {p}
                    </p>
                  ))}
                  {s.list && (
                    <ul className="space-y-3">
                      {s.list.map((li) => (
                        <li key={li} className="flex items-baseline gap-3 text-sm leading-relaxed text-slate-400">
                          <span className="inline-block h-1.5 w-1.5 shrink-0 rotate-45 border border-glow/60" aria-hidden="true" />
                          {li}
                        </li>
                      ))}
                    </ul>
                  )}
                  {s.after && <p className="text-sm leading-relaxed text-slate-400">{s.after}</p>}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
